import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, lastValueFrom } from 'rxjs';
import { VenuesApiService } from './venues-api.service';
import { VenueResponse } from '../../core/models/VenueResponse';
import { Venue } from '../../core/classes/Venue';
import { OrderMode } from '../../core/enums/order/OrderMode';
import { sortVenuesByDistance } from '../../shared/utils/geo';
import { VenuesByOrder } from '../../core/models/VenuesByOrder';
import { Address } from '../../core/classes/Address';
import { Legal } from '../../core/models/Legal';
import { AddressDefineMode } from '../../domains/maps/maps.component';

@Injectable({
	providedIn: 'root',
})
export class VenuesStateService {
	readonly initialVenuesByOrderMode: VenuesByOrder = {
		[OrderMode.INSIDE]: [],
		[OrderMode.DELIVERY]: {
			all: [],
			byDeliveryAddress: [],
		},
		[OrderMode.TAKE_AWAY]: [],
		[OrderMode.PARK_COLLECT]: [],
	};

	allVenues$: BehaviorSubject<Venue[]> = new BehaviorSubject([]);
	venuesByOrderMode$: BehaviorSubject<VenuesByOrder> = new BehaviorSubject(
		structuredClone(this.initialVenuesByOrderMode)
	);
	selectedVenue$: BehaviorSubject<Venue> = new BehaviorSubject(null);
	selectedOrderMode$ = new BehaviorSubject(OrderMode.INSIDE);

	legal: Legal;
	addressDefineMode: AddressDefineMode = null;

	private venuesApi = inject(VenuesApiService);
	constructor() {}

	actualVenues(deliveryAddress: Address) {
		const actualVenues = this.venuesByOrderMode$.value[this.selectedOrderMode$.value];
		if (this.selectedOrderMode$.value === OrderMode.DELIVERY) {
			const deliveryVenues = actualVenues as VenuesByOrder[OrderMode.DELIVERY];
			return deliveryAddress ? deliveryVenues.byDeliveryAddress : deliveryVenues.all;
		} else return actualVenues as Venue[];
	}

	isVenueAvailable(venue: VenueResponse) {
		if (!venue.isServiceActivated || !venue.isPublished || !venue?.slot?.active) {
			return false;
		}
		if (!venue?.location?.coordinates[0] || !venue?.location?.coordinates[1]) return false;
		if (this.selectedOrderMode$.value === OrderMode.DELIVERY && !venue.deliveryHours.length) return false;

		return !venue.isMaster;
	}

	async initVenues(userAddress: Address) {
		const sortedVenuesByOrderMode = structuredClone(this.initialVenuesByOrderMode);
		let allVenues: Venue[] = this.allVenues$.value;

		if (!allVenues.length) {
			const responseVenues: VenueResponse[] = await lastValueFrom(this.venuesApi.getAllVenues());
			responseVenues.forEach((venueRes): any => {
				if (!this.isVenueAvailable(venueRes)) return;
				allVenues.push(new Venue(venueRes, userAddress, this.selectedOrderMode$.value));
			});
		}

		if (userAddress) {
			allVenues.forEach(v => (v.distanceToUser = v.computeDistanceToUser(userAddress)));
			allVenues = sortVenuesByDistance(allVenues);
		}

		allVenues.forEach((venue: Venue): any => {
			if (venue.preorderTakeAway) sortedVenuesByOrderMode[OrderMode.TAKE_AWAY].push(venue);
			if (venue.preorderIn) sortedVenuesByOrderMode[OrderMode.INSIDE].push(venue);
			if (venue.deliveryEnabled) {
				// if (venue.deliveryByRadius && venue.distanceToUser !== undefined && venue.distanceToUser > venue.deliveryRadius) {
				//     return false;
				// }
				// if (venue.deliveryPostalCodes && !venue.deliveryByRadius && !venue.isPostalDelivery) {
				//     return false;
				// }
				sortedVenuesByOrderMode[OrderMode.DELIVERY].all.push(venue);
			}
		});

		this.allVenues$.next(allVenues);
		this.selectedVenue$.next(userAddress ? sortedVenuesByOrderMode[this.selectedOrderMode$.value]?.[0] : null);
		this.venuesByOrderMode$.next(sortedVenuesByOrderMode);
	}

	async loadVenuesForDeliveryAddress(address: Address) {
		let responseVenues: VenueResponse[] = await lastValueFrom(
			this.venuesApi.getVenuesByGeo(address.lat, address.lng, 25, false)
		);
		let venues = responseVenues
			.filter(vR => this.isVenueAvailable(vR))
			.map(vR => new Venue(vR, address, this.selectedOrderMode$.value));
		venues = sortVenuesByDistance(venues);

		return venues;
	}

	async initVenuesForDeliveryAddress(address: Address) {
		const venues = await this.loadVenuesForDeliveryAddress(address);
		if (!venues.length) throw new Error('No venue For this address');

		const venuesByOrderMode = this.venuesByOrderMode$.value;
		venuesByOrderMode[OrderMode.DELIVERY].byDeliveryAddress = venues;
		this.venuesByOrderMode$.next(venuesByOrderMode);

		this.selectedVenue$.next(this.actualVenues(address)[0]);
	}

	async initLegal() {
		if (this.legal) return;
		this.legal = await firstValueFrom(this.venuesApi.getLegal('6a577c932adc495b67635739'));
	}
}
